import React, { useEffect, useState, useCallback } from "react";
import "./ChatStyles.css";
import SidebarLayout from "../../common/sidebar/SidebarLayout";
import ChatListing from "./ChatListing";
import ChatTexting from "./ChatTexting";
import { getFullName, notify } from "../../../util/helpers";
import NoSelectedText from "./NoSelectedText";
import { useDispatch, useSelector } from "react-redux";
import {
  getChatEnabledPatientsLogic,
  getChatListingLogic,
  getSinglePatientChat,
  getUnreadMessagesLogic,
  getUnreadPatientsChatLogic,
  markAsReadLogic,
} from "../../../store/features/chat/chatSlice";
import AgoraClientComponent, {
  chatStructureHelper,
  sendMessage,
} from "./AgoraMethods";
import { isEmptyObject } from "jquery";
import IncomingMessage from "../../../assets/sounds/IncomingMessage.mp3";
import DummyClient from "./DummyClient";
import { Helmet } from "react-helmet";
import PatientLoader from "../../common/PatientLoader";

const Chat = (props) => {
  const dispatch = useDispatch();
  const [chatData, setChatData] = useState(null);
  const [remoteShow, setRemoteShow] = useState(false);
  const [chatPatients, setChatPatients] = useState([]);
  const [singleChatLoading, setSingleChatLoading] = useState(false);
  const [chatEnabledPatients, setChatEnabledPatients] = useState([]);
  const [latestMessage, setLatestMessage] = useState(false);
  const [inChatMessage, setInChatMessage] = useState(null);
  const [clinicianChatMessage, setClinicianChatMessage] = useState(null);

  const { user, selectedClinic, selectedProvider } = useSelector(
    (state) => state.app
  );
  const {
    chatListing,
    chatListingLoading: chatLoading,
    chatEnabledPatientsData,
    chatEnabledPatientsLoading,
    chatEnabledPatientsErrors,
  } = useSelector((state) => state.chat);

  const clinicianId = selectedProvider ? selectedProvider : user?.id;
  const clinicianName = getFullName(user);
  const clinicAdmin = selectedClinic?.clinic_admin;

  const setSinglePatientChatData = useCallback((
    singleChatData = null,
    patient_id = null
  ) => {
    setChatData(null);
    setSingleChatLoading(true);
    let patientID = patient_id;
    if (singleChatData) {
      patientID = singleChatData?.patient_id;
    }

    // Optimistically clear the unread count badge on the frontend immediately
    setChatPatients((prevPatients) => {
      const chatTile = prevPatients.findIndex(
        (chat) => chat?.patient_id === parseInt(patientID)
      );
      if (chatTile !== -1) {
        const temp = JSON.parse(JSON.stringify(prevPatients));
        temp[chatTile].count = 0;
        temp[chatTile].unread_count = 0;
        return temp;
      }
      return prevPatients;
    });

    //*Getting all chats for this patient
    getSinglePatientChat({
      to: patientID,
      sender: patientID,
      page_size: "9999999",
    })
      .then((res) => {
        if (res.status === 200) {
          let formattedData = {};
          const messages = res.data?.results;

          if (messages?.length > 0) {
            sessionStorage.setItem("latestMsgId_" + patientID, messages[0].id.toString());
            messages.forEach((item) => {
              chatStructureHelper(
                item,
                formattedData,
                clinicianId,
                clinicianName,
                clinicAdmin
              );
            });
          }
          sessionStorage.removeItem("openChatID");
          sessionStorage.setItem("openChatID", patientID.toString());

          if (isEmptyObject(formattedData)) {
            setChatData(singleChatData);
            if (singleChatData) {
              setChatPatients((prevPatients) => [singleChatData, ...prevPatients]);
            }
          } else {
            setChatData(formattedData[patientID]);
          }
        } else {
          notify(
            "Something went wrong while fetching messages, try again later!",
            "error"
          );
        }
      })
      .catch((e) => {
        console.log(e);
      });
    setSingleChatLoading(false);

    //*Marking the messages as read for this patient
    markAsReadLogic(patientID)
      .then((res) => {
        if (res.status === 200) {
          //* Removing unread message counter badge
          setChatPatients((prevPatients) => {
            const chatTile = prevPatients.findIndex(
              (chat) => chat?.patient_id === parseInt(patientID)
            );
            if (chatTile !== -1) {
              const temp = JSON.parse(JSON.stringify(prevPatients));
              temp[chatTile].count = 0;
              temp[chatTile].unread_count = 0;
              return temp;
            }
            return prevPatients;
          });
          dispatch(getUnreadPatientsChatLogic(selectedClinic?.clinic?.id));
          dispatch(getUnreadMessagesLogic());
        }
      })
      .catch((e) => {
        console.log("Something went wrong while marking message as read", e);
      });
  }, [clinicianId, clinicianName, clinicAdmin, selectedClinic, dispatch]);

  useEffect(() => {
    setChatData(null); //* Without this, if a chat was selected, it remained on screen even when loader was spinning to get new data.
    dispatch(
      getChatEnabledPatientsLogic({
        clinic_id: selectedClinic?.clinic?.id,
        clinician_id: clinicianId,
      })
    );
    dispatch(getUnreadMessagesLogic());
    dispatch(getUnreadPatientsChatLogic(selectedClinic?.clinic?.id));
    sessionStorage.removeItem("openChatID");

    return () => {
      const openId = sessionStorage.getItem("openChatID");
      if (openId) {
        markAsReadLogic(openId)
          .then(() => {
            dispatch(getUnreadPatientsChatLogic(selectedClinic?.clinic?.id));
            dispatch(getUnreadMessagesLogic());
          })
          .catch((e) => console.log("Error marking as read on unmount", e));
      }
      setChatData(null);
      sessionStorage.removeItem("openChatID");
    };
  }, []);

  // Polling fallback to handle cases where RTM fails (e.g. Older Mobile App's 3-message timeout)
  useEffect(() => {
    const pollInterval = setInterval(() => {
      // 1. Refresh the sidebar (page 1) to pick up new messages and update unread counts
      if (clinicianId && selectedClinic?.clinic?.id) {
        dispatch(getChatListingLogic({
          page: 1,
          page_size: "10",
          search: "",
          clinic_id: selectedClinic?.clinic?.id,
          clinician_id: clinicianId
        }));
      }

      // 2. If a chat is currently open, refresh its history silently
      const openChatID = sessionStorage.getItem("openChatID");
      if (openChatID) {
        getSinglePatientChat({
          to: openChatID,
          sender: openChatID,
          page_size: "50",
        }).then((res) => {
          if (res.status === 200) {
            const messages = res.data?.results;
            if (messages?.length > 0) {
              const storedId = sessionStorage.getItem("latestMsgId_" + openChatID);
              
              let newMessages = [];
              if (storedId) {
                for (let msg of messages) {
                  if (msg.id.toString() === storedId) break;
                  newMessages.push(msg);
                }
              } else {
                newMessages.push(messages[0]);
              }

              if (newMessages.length > 0) {
                newMessages.reverse(); // Process oldest to newest
                
                newMessages.forEach((latestMsg, index) => {
                  setTimeout(() => {
                    sessionStorage.setItem("latestMsgId_" + openChatID, latestMsg.id.toString());
                    
                    if (String(latestMsg.sender) !== String(clinicianId)) {
                      let recentRTMs = JSON.parse(sessionStorage.getItem("recentRTMs_" + openChatID) || "[]");
                      const rtmIndex = recentRTMs.indexOf(latestMsg.message);

                      if (rtmIndex !== -1) {
                        // Already rendered via RTM. Remove from queue and skip.
                        recentRTMs.splice(rtmIndex, 1);
                        sessionStorage.setItem("recentRTMs_" + openChatID, JSON.stringify(recentRTMs));
                      } else {
                        // Missed by RTM, append it now
                        setInChatMessage({
                          text: latestMsg.message,
                          messageType: latestMsg.message_type?.toUpperCase() || "TEXT"
                        });
                      }
                    }
                  }, index * 250); // 250ms delay bypasses React batching
                });
              }
            }
          }
        }).catch(e => console.log("Polling history error", e));
      }
    }, 60000); // Background sync every 60 seconds

    return () => clearInterval(pollInterval);
  }, [clinicianId, selectedClinic, clinicianName, clinicAdmin, dispatch]);

  useEffect(() => {
    const handleWakeUp = () => {
      if (document.visibilityState === "visible") {
        console.log("Device woke up / window focused. Syncing chat data...");
        // Refresh unread counts
        dispatch(getUnreadMessagesLogic());
        if (selectedClinic?.clinic?.id) {
          dispatch(getUnreadPatientsChatLogic(selectedClinic.clinic.id));
        }
        // Refresh chat listing
        if (clinicianId && selectedClinic?.clinic?.id) {
          dispatch(getChatListingLogic({
            page: 1,
            page_size: "10",
            search: "",
            clinic_id: selectedClinic?.clinic?.id,
            clinician_id: clinicianId
          }));
        }
        // Refresh single chat history if open
        const openChatID = sessionStorage.getItem("openChatID");
        if (openChatID) {
          setSinglePatientChatData(null, openChatID);
        }
      }
    };

    document.addEventListener("visibilitychange", handleWakeUp);
    window.addEventListener("focus", handleWakeUp);

    return () => {
      document.removeEventListener("visibilitychange", handleWakeUp);
      window.removeEventListener("focus", handleWakeUp);
    };
  }, [clinicianId, selectedClinic, dispatch, setSinglePatientChatData]);


  const handlePeerMessage = useCallback((message, peerID) => {
    //* If the chat is currently open
    let openChatID = sessionStorage.getItem("openChatID");
    if (!openChatID) {
      openChatID = chatData?.patient_id;
      sessionStorage.setItem("openChatID", chatData?.patient_id);
    }

    if (openChatID === peerID) {
      // here set message for that comes from client,Now,I have to set the message as read.
      setInChatMessage(message);
      
      // Store this RTM message so the polling loop knows we already rendered it
      let recentRTMs = JSON.parse(sessionStorage.getItem("recentRTMs_" + peerID) || "[]");
      recentRTMs.push(message.text);
      sessionStorage.setItem("recentRTMs_" + peerID, JSON.stringify(recentRTMs));

      //*Marking the messages as read for this patient (1s delay to prevent race condition with DB save)
      setTimeout(() => {
        markAsReadLogic(openChatID)
          .then((res) => {
            if (res.status === 200) {
              console.log(res, "response to mark as read logic");
              //* Removing unread message counter badge using functional state updater to avoid stale state
              setChatPatients((prevPatients) => {
                const chatTile = prevPatients.findIndex(
                  (chat) => chat?.patient_id === parseInt(peerID)
                );
                if (chatTile !== -1) {
                  const temp = JSON.parse(JSON.stringify(prevPatients));
                  temp[chatTile].count = 0;
                  temp[chatTile].unread_count = 0;
                  return temp;
                }
                return prevPatients;
              });
              dispatch(getUnreadPatientsChatLogic(selectedClinic?.clinic?.id));
              dispatch(getUnreadMessagesLogic());
            }
          })
          .catch((e) => {
            console.log("Something went wrong while marking message as read", e);
          });
      }, 1000);

      //* Now update the sidebar. This is done regardless of whether the chat is open or not.
      setLatestMessage({
        message: message,
        peerID: peerID,
        updateBadge: false,
      });
    } else {
      //* Update the sidebar.
      setLatestMessage({
        message: message,
        peerID: peerID,
        updateBadge: true,
      });
    }
  }, [chatData, selectedClinic, dispatch]);



  useEffect(() => {
    if (!chatEnabledPatientsLoading && !chatEnabledPatientsErrors) {
      setChatData(null);
      setChatEnabledPatients(chatEnabledPatientsData?.results);
    }
  }, [
    chatEnabledPatientsLoading,
    chatEnabledPatientsData,
    chatEnabledPatientsErrors,
  ]);


  return (
    <SidebarLayout {...props}>
      <Helmet>
        <meta charSet="utf-8" />
        <title>Chat | Precise Digital</title>
        <link rel="canonical" href={window.location.origin} />
      </Helmet>
      <div className="container-fluid bg-white py-3">
        <audio src={IncomingMessage} id="incomingMessageSound" muted autoPlay />
        {/* <DummyClient /> */}
        <AgoraClientComponent
          callback={handlePeerMessage}
          setRemoteShow={setRemoteShow}
        />
        <div className="container-fluid d-flex justify-content-start">
          <h3 className="primary-text-blue">Chats</h3>
        </div>
        <div className=" container-fluid my-2 row">
          <ChatListing
            chatPatients={chatPatients}
            setChatData={setSinglePatientChatData}
            chatEnabledPatients={chatEnabledPatients}
            latestMessage={latestMessage}
            setChatPatients={setChatPatients}
          />

          {chatData ? (
            <ChatTexting
              name={chatData?.name}
              risk={chatData?.flag}
              patientID={chatData?.patient_id}
              providerData={{
                name: clinicianName,
                type: "Primary Provider",
                clinicianId: clinicianId,
              }}
              closeChat={() => {
                sessionStorage.removeItem("openChatID");
                setChatData(null);
              }}
              chatHistory={chatData?.messages}
              singleChatLoading={singleChatLoading}
              sendMessage={sendMessage}
              inChatMessage={inChatMessage}
              setInChatMessage={setInChatMessage}
              chatData={chatData}
              clinicianChatMessage={clinicianChatMessage}
              setClinicianChatMessage={setClinicianChatMessage}
              setLatestMessage={setLatestMessage}
            />
          ) : (
            <>
              {singleChatLoading ? <></> : <NoSelectedText />}
            </>
          )}
        </div>
      </div>
    </SidebarLayout>
  );
};

export default Chat;
